import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Edit3,
  Trash2,
  Clock,
  User,
  Phone,
  AlertTriangle,
  Check,
  Stethoscope
} from 'lucide-react';
import { ClientMedicalInfoDto, getMedicalConditionStatusColor } from '../../services/medicalConditionsService';
import { hexToRgba } from '../../utils/colorUtils';

interface MedicalConditionCardProps {
  condition: ClientMedicalInfoDto;
  onEdit: () => void;
  onDelete: () => void;
  formatDate: (dateString?: string) => string;
}

const Item = styled.div<{ $isPrimary: boolean }>`
  padding: 18px 24px;
  border-right: 4px solid ${props => props.$isPrimary ? '#dc2626' : '#94a3b8'};
  border-bottom: 1px solid var(--border-color);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const TitleSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const DiseaseName = styled.h4`
  margin: 0 0 6px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TimeSensitiveIcon = styled.span`
  color: #d97706;
  display: flex;
  align-items: center;
`;

const CategoryName = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const TypeBadge = styled.span<{ $isPrimary: boolean }>`
  background: ${props => props.$isPrimary ? '#dc2626' : '#94a3b8'};
  color: white;
  padding: 5px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 700;
`;

const StatusBadge = styled.span<{ $color: string }>`
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
  border: 1px solid ${props => hexToRgba(props.$color, 0.25)};
  padding: 5px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 700;
`;

const UrgentBadge = styled.span`
  background: rgba(217, 119, 6, 0.12);
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.25);
  padding: 5px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ItemBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: var(--text-secondary);
`;

const InfoIcon = styled.span`
  color: var(--primary-color);
  opacity: 0.8;
  display: flex;
  align-items: center;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 600;
`;

const HistoryFlags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);
`;

const HistoryFlag = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #16a34a;
  font-weight: 600;
`;

const NotesSection = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);
`;

const NotesLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const NotesText = styled.p`
  margin: 6px 0 0 0;
  font-size: 15px;
  color: var(--text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const ActionButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'delete' ? `
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;

    &:hover {
      background: #dc2626;
      color: white;
    }
  ` : `
    background: rgba(0, 115, 234, 0.1);
    color: var(--primary-color);

    &:hover {
      background: var(--primary-color);
      color: white;
    }
  `}
`;

/* ========== Confirmation Modal ========== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  margin: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DiseaseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const DiseaseIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: rgba(0, 115, 234, 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
`;

const DiseaseDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DiseaseNameDisplay = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const DiseaseCategory = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const ModalWarning = styled.p`
  margin: 0;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.5;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 8px;
`;

const ModalButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'danger' ? `
    background: #dc2626;
    color: white;
    border: none;

    &:hover {
      background: #b91c1c;
    }
  ` : `
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
      border-color: #d1d5db;
    }
  `}
`;

const MedicalConditionCard: React.FC<MedicalConditionCardProps> = ({
  condition,
  onEdit,
  onDelete,
  formatDate
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    onDelete();
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const hasHistoryFlags = condition.wasHospitalizedForThisDisease ||
                          condition.hadSurgeryForThisDisease ||
                          condition.submittedClaimForThisDisease;

  return (
    <Item $isPrimary={condition.isPrimary}>
      <ItemHeader>
        <TitleSection>
          <DiseaseName>
            {condition.diseaseName}
            {condition.isTimeSensitive && (
              <TimeSensitiveIcon title="רגיש לזמן">
                <AlertTriangle size={16} strokeWidth={2} />
              </TimeSensitiveIcon>
            )}
          </DiseaseName>
          <CategoryName>{condition.diseaseCategoryName}</CategoryName>
        </TitleSection>

        <BadgeGroup>
          <TypeBadge $isPrimary={condition.isPrimary}>
            {condition.diseaseType}
          </TypeBadge>
          {condition.statusName && condition.status && (
            <StatusBadge $color={getMedicalConditionStatusColor(condition.status)}>
              {condition.statusName}
            </StatusBadge>
          )}
          {condition.isTimeSensitive && (
            <UrgentBadge>
              <Clock size={12} strokeWidth={2} />
              דחוף
            </UrgentBadge>
          )}
        </BadgeGroup>
      </ItemHeader>

      <ItemBody>
        {condition.diagnosisDate && (
          <InfoItem>
            <InfoIcon><Clock size={14} strokeWidth={2} /></InfoIcon>
            <span>אבחון:</span>
            <InfoValue>{formatDate(condition.diagnosisDate)}</InfoValue>
          </InfoItem>
        )}

        {condition.physicianName && (
          <InfoItem>
            <InfoIcon><User size={14} strokeWidth={2} /></InfoIcon>
            <span>רופא:</span>
            <InfoValue>{condition.physicianName}</InfoValue>
          </InfoItem>
        )}

        {condition.physicianPhone && (
          <InfoItem>
            <InfoIcon><Phone size={14} strokeWidth={2} /></InfoIcon>
            <InfoValue>{condition.physicianPhone}</InfoValue>
          </InfoItem>
        )}
      </ItemBody>

      {hasHistoryFlags && (
        <HistoryFlags>
          {condition.wasHospitalizedForThisDisease && (
            <HistoryFlag>
              <Check size={14} strokeWidth={2} />
              אושפז
            </HistoryFlag>
          )}
          {condition.hadSurgeryForThisDisease && (
            <HistoryFlag>
              <Check size={14} strokeWidth={2} />
              עבר ניתוח
            </HistoryFlag>
          )}
          {condition.submittedClaimForThisDisease && (
            <HistoryFlag>
              <Check size={14} strokeWidth={2} />
              הוגשה תביעה
            </HistoryFlag>
          )}
        </HistoryFlags>
      )}

      {condition.notes && (
        <NotesSection>
          <NotesLabel>הערות</NotesLabel>
          <NotesText>{condition.notes}</NotesText>
        </NotesSection>
      )}

      <Actions>
        <ActionButton onClick={onEdit}>
          <Edit3 size={14} strokeWidth={2} />
          עריכה
        </ActionButton>
        <ActionButton $variant="delete" onClick={handleDeleteClick}>
          <Trash2 size={14} strokeWidth={2} />
          מחיקה
        </ActionButton>
      </Actions>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalOverlay onClick={handleCancelDelete}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>מחיקת מחלה</ModalTitle>
              <ModalCloseButton onClick={handleCancelDelete}>✕</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DiseaseInfo>
                <DiseaseIcon><Stethoscope size={24} strokeWidth={2} /></DiseaseIcon>
                <DiseaseDetails>
                  <DiseaseNameDisplay>{condition.diseaseName}</DiseaseNameDisplay>
                  <DiseaseCategory>
                    {condition.diseaseCategoryName}
                    {condition.isPrimary && ' • מחלה עיקרית'}
                  </DiseaseCategory>
                </DiseaseDetails>
              </DiseaseInfo>

              <ModalWarning>
                פעולה זו אינה ניתנת לביטול. כל המידע הקשור למחלה זו יימחק לצמיתות.
              </ModalWarning>

              <ModalButtonGroup>
                <ModalButton $variant="danger" onClick={handleConfirmDelete}>
                  מחק מחלה
                </ModalButton>
                <ModalButton onClick={handleCancelDelete}>
                  ביטול
                </ModalButton>
              </ModalButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Item>
  );
};

export default MedicalConditionCard;
